import React, { useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useToast } from '../../contexts/ToastContext';
import { UsageEventsTracker } from '../../services/UsageEventsTracker';
import { THEME_META, THEME_DISPLAY_ORDER } from '../../theme/themes';
import './ThemeSelectorModal.css';

/* ── Mini Dashboard Preview ────────────────────────────────────────── */
function MiniThemePreview({ preview }) {
  const gradientBar = `linear-gradient(to right, ${preview.accentBg}, ${preview.headerBg})`;
  return (
    <div className="tsm-mini" style={{ background: preview.pageBg }}>
      {/* Accent line at top */}
      <div className="tsm-mini__accent" style={{ background: preview.accentBg }} />

      {/* Header zone: welcome bar + theme strip */}
      <div className="tsm-mini__hero" style={{
        background: `linear-gradient(135deg,
          color-mix(in srgb, ${preview.accentBg} 5%, ${preview.cardBg}) 0%,
          ${preview.cardBg} 100%)`
      }}>
        <div className="tsm-mini__welcome" style={{ background: preview.textPrimary }} />
        <div className="tsm-mini__themestrip" style={{ background: gradientBar }} />
        <div className="tsm-mini__date" style={{ background: preview.textSecondary }} />
      </div>

      {/* KPI tiles row */}
      <div className="tsm-mini__tiles">
        <div className="tsm-mini__tile" style={{ background: preview.cardBg, borderColor: preview.textSecondary }}>
          <div className="tsm-mini__tile-label" style={{ background: preview.textSecondary }} />
          <div className="tsm-mini__tile-value" style={{ background: preview.textPrimary }} />
        </div>
        <div className="tsm-mini__tile" style={{ background: preview.cardBg, borderColor: preview.textSecondary }}>
          <div className="tsm-mini__tile-label" style={{ background: preview.textSecondary }} />
          <div className="tsm-mini__tile-value" style={{ background: preview.accentBg }} />
        </div>
        <div className="tsm-mini__tile" style={{ background: preview.cardBg, borderColor: preview.textSecondary }}>
          <div className="tsm-mini__tile-label" style={{ background: preview.textSecondary }} />
          <div className="tsm-mini__tile-value" style={{ background: preview.textPrimary }} />
        </div>
      </div>
    </div>
  );
}

/* ── Theme Option Card ─────────────────────────────────────────────── */
function ThemeOptionCard({ meta, isActive, onSelect }) {
  const p = meta.preview;
  const cardVars = {
    '--option-page-bg': p.pageBg,
    '--option-card-bg': p.cardBg,
    '--option-text': p.textPrimary,
    '--option-text-secondary': p.textSecondary,
    '--option-accent': p.accentBg,
    '--option-accent-text': p.accentText,
    '--option-header': p.headerBg,
  };
  return (
    <button
      type="button"
      className={`tsm-option${isActive ? ' tsm-option--active' : ''}`}
      onClick={() => onSelect(meta.id)}
      aria-pressed={isActive}
      style={cardVars}
    >
      {/* Active indicator */}
      {isActive && (
        <span className="tsm-option__badge" aria-label="Current theme">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          Current
        </span>
      )}

      {/* Preview */}
      <MiniThemePreview preview={p} />

      {/* Info */}
      <div className="tsm-option__info">
        <span className="tsm-option__name">{meta.displayName}</span>
        {meta.description && <span className="tsm-option__desc">{meta.description}</span>}
      </div>

      {/* Swatches */}
      <div className="tsm-option__swatches">
        {meta.swatches.slice(0, 6).map((color, i) => (
          <span key={i} className="tsm-option__swatch" style={{ background: color }} title={color} />
        ))}
      </div>
    </button>
  );
}

/* ── Modal ─────────────────────────────────────────────────────────── */
export default function ThemeSelectorModal({ open, onClose }) {
  const { currentTheme, setTheme } = useTheme();
  const { patchPreferences } = usePreferences();
  const { addToast } = useToast();
  const backdropRef = useRef(null);
  const prevThemeRef = useRef(currentTheme);

  // Store the theme at open time so we report accurate "previous"
  useEffect(() => {
    if (open) prevThemeRef.current = currentTheme;
  }, [open, currentTheme]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Close on backdrop click
  const handleBackdrop = useCallback((e) => {
    if (e.target === backdropRef.current) onClose();
  }, [onClose]);

  const handleSelect = useCallback(async (themeId) => {
    if (themeId === currentTheme) return;
    const prev = currentTheme;
    setTheme(themeId);

    // Track
    UsageEventsTracker.recordQueued({
      eventType: 'THEME_CHANGE',
      payload: { action: 'change', category: 'theme', entityType: 'theme', entityId: themeId, label: 'Theme Selector Modal', metadata: { previous: prev, next: themeId } },
    });
    UsageEventsTracker.flush();

    // Persist
    try {
      await patchPreferences({ theme: themeId });
    } catch (e) {
      console.warn('[ThemeSelectorModal] Failed to persist theme', e);
    }

    addToast({ message: 'Theme updated', intent: 'success', duration: 3000 });
  }, [currentTheme, setTheme, patchPreferences, addToast]);

  if (!open) return null;

  const ordered = THEME_DISPLAY_ORDER.filter(id => THEME_META[id]);

  // Categorize into light vs dark for small-screen rows
  const DARK_IDS = new Set([
    'docuHounds', 'darkLab', 'retroVibes',
    'midnightGold', 'velvetNight', 'steelCore', 'neonCircuit', 'crimsonFocus',
  ]);
  const lightThemes = ordered.filter(id => !DARK_IDS.has(id));
  const darkThemes  = ordered.filter(id => DARK_IDS.has(id));

  return ReactDOM.createPortal(
    <div className="tsm-backdrop" ref={backdropRef} onClick={handleBackdrop}>
      <div className="tsm-modal" role="dialog" aria-modal="true" aria-label="Choose Theme">
        {/* Header */}
        <div className="tsm-modal__header">
          <div className="tsm-modal__header-text">
            <h2 className="tsm-modal__title">Choose Theme</h2>
            <p className="tsm-modal__subtitle">Pick a look that fits how you work</p>
          </div>
          <button type="button" className="tsm-modal__close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Desktop: sectioned grids */}
        <div className="tsm-sections tsm-sections--desktop">
          <div className="tsm-section">
            <h3 className="tsm-section__label">Light Themes</h3>
            <div className="tsm-grid">
              {lightThemes.map(id => (
                <ThemeOptionCard key={id} meta={THEME_META[id]} isActive={currentTheme === id} onSelect={handleSelect} />
              ))}
            </div>
          </div>
          <div className="tsm-section tsm-section--dark">
            <h3 className="tsm-section__label">Dark Themes</h3>
            <div className="tsm-grid">
              {darkThemes.map(id => (
                <ThemeOptionCard key={id} meta={THEME_META[id]} isActive={currentTheme === id} onSelect={handleSelect} />
              ))}
            </div>
          </div>
        </div>

        {/* Mobile: categorized horizontal scroll rows */}
        <div className="tsm-rows tsm-rows--mobile">
          <div className="tsm-row-section">
            <h3 className="tsm-row-section__label">Light Themes</h3>
            <div className="tsm-row">
              {lightThemes.map(id => (
                <ThemeOptionCard key={id} meta={THEME_META[id]} isActive={currentTheme === id} onSelect={handleSelect} />
              ))}
            </div>
          </div>
          <div className="tsm-row-section">
            <h3 className="tsm-row-section__label">Dark Themes</h3>
            <div className="tsm-row">
              {darkThemes.map(id => (
                <ThemeOptionCard key={id} meta={THEME_META[id]} isActive={currentTheme === id} onSelect={handleSelect} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
